import { useAppDispatch } from '$hooks/use-app-dispatch.hook';
import { useAppSelector } from '$hooks/use-app-selector.hook';
import useOnClickOutside from '$hooks/useOnClickOutside';
import { ReactComponent as DownArrow } from '$icons/ArrowDown.svg';
import { setSelectedCountryFilter } from '$store/modules/internal-filters/internal-filters.reducer';
import { toPascalCase } from '$utils/toPascalCase';
import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { InternalFiltersState } from '$store/modules/internal-filters/internal-filters.types';

type FilterMultiSelectWithGroupsProps = {
  title: string;
  data: { [key: string]: string[] };
  hideUnknownCheckbox?: boolean;
};

const ParentCheckbox = ({
  title,
  childs,
  queryStringParamName,
}: {
  title: string;
  childs: string[];
  queryStringParamName: keyof InternalFiltersState;
}) => {
  const firstLoad = useRef(true);
  const lastSearchCountryParam = useRef<undefined | number>();
  const inputRef = useRef<HTMLInputElement>(null);
  const [isParentExpanded, setIsParentExpanded] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedChilds, setSelectedChilds] = useState<string[]>(
    childs.filter((singleChild) => {
      const countryArr = searchParams.get(queryStringParamName)?.split(',');
      return (
        countryArr &&
        (countryArr.indexOf(singleChild) >= 0 || countryArr.indexOf(title) >= 0)
      );
    })
  );
  const dispatch = useAppDispatch();
  const oldData = useAppSelector((state) => state.internalFilter)?.[
    queryStringParamName
  ];

  const handleChangeParentCheckbox = () => {
    if (inputRef.current) {
      if (inputRef.current.checked && selectedChilds.length === 0) {
        inputRef.current.checked = true;
        inputRef.current.indeterminate = false;
        setSelectedChilds([...childs]);
        dispatch(setSelectedCountryFilter([...oldData, ...childs]));
      } else {
        const newData = oldData.filter(
          (item: string) =>
            !selectedChilds.some((singleChild) => singleChild === item)
        );
        dispatch(setSelectedCountryFilter(newData));
        setSelectedChilds([]);
        inputRef.current.checked = false;
        inputRef.current.indeterminate = false;
      }
    }
  };

  const handleIndeterminate = useCallback(
    (newSelection: string[]) => {
      if (inputRef.current) {
        if (newSelection.length === 0) {
          inputRef.current.indeterminate = false;
          inputRef.current.checked = false;
        } else if (newSelection.length !== childs.length) {
          inputRef.current.indeterminate = true;
          inputRef.current.checked = true;
        } else if (newSelection.length === childs.length) {
          inputRef.current.indeterminate = false;
          inputRef.current.checked = true;
        }
      }
    },
    [childs.length]
  );

  const childChangeHandler = (ev: ChangeEvent<HTMLInputElement>) => {
    if (ev.target.checked) {
      // Added new element
      setSelectedChilds((prev) => {
        const result = [...prev, ev.target.value];
        handleIndeterminate(result);
        return result;
      });
      dispatch(setSelectedCountryFilter([...oldData, ev.target.value]));
    } else {
      // Removed a element
      setSelectedChilds((prev) => {
        const result = prev.filter((item) => item !== ev.target.value);
        handleIndeterminate(result);
        return result;
      });
      dispatch(
        setSelectedCountryFilter(
          oldData.filter((item: string) => item !== ev.target.value)
        )
      );
    }
  };

  useEffect(() => {
    if (firstLoad.current) {
      firstLoad.current = false;
      const urlParams = new URLSearchParams(searchParams);
      const countryParam = urlParams.get(queryStringParamName);
      if (countryParam) {
        handleIndeterminate(selectedChilds);
      }
    }
  }, [
    dispatch,
    handleIndeterminate,
    oldData,
    queryStringParamName,
    searchParams,
    selectedChilds,
  ]);

  useEffect(() => {
    const newUrlQuerystring = new URLSearchParams(searchParams);
    const countryParams = searchParams.get(queryStringParamName)?.split(',');

    if (lastSearchCountryParam.current !== selectedChilds.length) {
      if (countryParams) {
        if (selectedChilds.length === childs.length) {
          const newParams = countryParams.filter(
            (singleCountryParam) => !!childs.indexOf(singleCountryParam)
          );
          newUrlQuerystring.set(
            queryStringParamName,
            [...newParams, title].toString()
          );
        } else if (selectedChilds.length > 0) {
          const newSelected = countryParams.filter(
            (singleCountryParam) => childs.indexOf(singleCountryParam) === -1
          );

          newUrlQuerystring.set(
            queryStringParamName,
            [...newSelected, ...selectedChilds].toString()
          );
        } else {
          const newParams = countryParams.filter(
            (singleCountryParam) =>
              !(
                childs.indexOf(singleCountryParam) >= 0 ||
                singleCountryParam === title
              )
          );

          if (newParams.length === 0)
            newUrlQuerystring.delete(queryStringParamName);
          else
            newUrlQuerystring.set(queryStringParamName, newParams.toString());
        }
      } else if (!countryParams && selectedChilds.length === childs.length) {
        newUrlQuerystring.set(queryStringParamName, title);
      } else if (!countryParams && selectedChilds.length > 0) {
        newUrlQuerystring.set(queryStringParamName, selectedChilds.toString());
      }
      setSearchParams(newUrlQuerystring);
      lastSearchCountryParam.current = selectedChilds.length;
    }
  }, [
    childs,
    queryStringParamName,
    searchParams,
    selectedChilds,
    setSearchParams,
    title,
  ]);

  return (
    <div className="px-4 my-1 bg-white z-50">
      <div className="flex flex-row gap-3 items-center">
        <button
          type="button"
          onClick={() => setIsParentExpanded((prev) => !prev)}
          className="flex justify-center w-4 h-4 border border-solid rounded border-gray-300 bg-gray-200 items-center"
        >
          {isParentExpanded ? '-' : '+'}
        </button>
        <label htmlFor={`${title}_parent_checkbox`} className="flex gap-1">
          <input
            type="checkbox"
            ref={inputRef}
            onChange={handleChangeParentCheckbox}
            id={`${title}_parent_checkbox`}
            checked={childs.every((singleChild) =>
              selectedChilds.includes(singleChild)
            )}
          />
          {title}
          {selectedChilds.length > 0 && ` (${selectedChilds.length})`}
        </label>
      </div>
      {isParentExpanded && (
        <ul className="list-inside flex flex-col gap-2">
          {childs.map((singleChildId: string) => {
            return (
              <li className="ml-2" key={`${singleChildId}_item`}>
                <label
                  htmlFor={`${singleChildId}_child_checkbox`}
                  className="flex gap-2 hover:bg-gray-200"
                >
                  <input
                    id={`${singleChildId}_child_checkbox`}
                    type="checkbox"
                    onChange={childChangeHandler}
                    value={singleChildId}
                    checked={
                      !!selectedChilds.some(
                        (item) => item === `${singleChildId}`
                      )
                    }
                  />
                  {singleChildId}
                </label>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

const FilterMultiSelectWithGroups = ({
  title,
  data,
  hideUnknownCheckbox = false,
}: FilterMultiSelectWithGroupsProps) => {
  const firstLoad = useRef(true);
  const [showMultiSelectOptions, setShowMultiSelectOptions] = useState(false);
  const [buttonTitle, setButtonTitle] = useState(title);
  const dispatch = useAppDispatch();
  const queryStringParamName = toPascalCase(
    title
  ) as keyof InternalFiltersState;
  const oldData = useAppSelector((state) => state.internalFilter)[
    queryStringParamName
  ];
  const multiSelectDivRef = useRef<HTMLDivElement>(null);
  const [searchParams] = useSearchParams();

  const unknownChangeHandler = (ev: ChangeEvent<HTMLInputElement>) => {
    let newData = [];

    if (ev.target.checked) {
      newData = [...oldData, 'UNKNOWN'];
    } else {
      newData = oldData.filter((item: number | string) => item !== 'UNKNOWN');
    }
    dispatch(setSelectedCountryFilter(newData));
  };

  useEffect(() => {
    if (oldData) {
      if (oldData.length > 0) {
        setButtonTitle(`${title} (${oldData.length})`);
      } else {
        setButtonTitle(title);
      }
    }
  }, [oldData, title]);

  useOnClickOutside(multiSelectDivRef, () => {
    setShowMultiSelectOptions(false);
  });

  useEffect(() => {
    if (firstLoad && firstLoad.current) {
      firstLoad.current = false;
      const countryParam = searchParams.get(queryStringParamName)?.split(',');
      if (countryParam) {
        const initialState = countryParam.reduce((prev, act) => {
          const parentGroupName = Object.keys(data);
          const matchedParentName = parentGroupName.find(
            (singleParentName) => singleParentName === act
          );

          if (matchedParentName) {
            prev = [...prev, ...data[matchedParentName]];
          } else {
            prev = [...prev, act];
          }
          return prev;
        }, [] as string[]);

        dispatch(setSelectedCountryFilter(initialState));
      }
    }
  }, [data, dispatch, queryStringParamName, searchParams]);

  return (
    <div ref={multiSelectDivRef}>
      <h1 className="font-semibold">{title}</h1>
      <div className="relative">
        <button
          type="button"
          className="flex flex-row bg-internal-main/10 py-2 px-3 rounded w-full text-black/40 justify-between"
          onClick={() => setShowMultiSelectOptions((prev) => !prev)}
        >
          {buttonTitle}
          <DownArrow />
        </button>
        {showMultiSelectOptions && (
          <div className="absolute shadow-md w-full max-h-96 rounded bg-white mt-2 border-gray-100 border py-2 z-50 overflow-y-auto select-none">
            {Object.keys(data).map((parentPropName) => {
              return (
                <ParentCheckbox
                  title={parentPropName}
                  childs={data[parentPropName]}
                  key={`parent_checkbox_${parentPropName}`}
                  queryStringParamName={queryStringParamName}
                />
              );
            })}
            {!hideUnknownCheckbox && (
              <div className="flex flex-row ml-4">
                <label
                  htmlFor="unknown_checkbox"
                  className="gap-2 flex flex-row"
                >
                  <input
                    id="unknown_checkbox"
                    type="checkbox"
                    onChange={unknownChangeHandler}
                    checked={oldData.some(
                      (singleCountry) =>
                        singleCountry.toLowerCase() === 'unknown'
                    )}
                  />
                  Unknown
                </label>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

FilterMultiSelectWithGroups.defaultProps = {
  hideUnknownCheckbox: false,
};

export default FilterMultiSelectWithGroups;
